// Relais Rybbit, branché sur le tracking Umami (voir lib/umami.js). Rybbit pose un
// identifiant visiteur dans le localStorage dès le chargement du script : il n'entre
// pas dans l'exemption CNIL de la mesure d'audience et n'est donc chargé qu'après le
// consentement `analytics` (service `rybbit` de cookie_consent_controller.js).
//
// Trois états : "unknown" tant que la bibliothèque de consentement n'a pas rendu son
// verdict (les controllers Stimulus se connectent avant, leurs évènements sont mis en
// file puis rejoués ou jetés), "accepted", "denied".
//
// Le script installe lui-même un stub de `window.rybbit` qui met les appels en file
// jusqu'à ce que sa configuration soit chargée : après son `load`, `event()` est sûr.

// Session replay (activé dans les réglages du site Rybbit, aucun attribut ne l'active) :
// tout texte et toute saisie sont masqués. Restent visibles la structure des pages, les
// clics, l'URL, l'état des cases cochées et les champs `hidden`, plus les métadonnées de
// session (pays, région, ville, langue, navigateur, OS, appareil), qui ne viennent pas du
// DOM. Le sélecteur "*" est joint tel quel dans le `maskTextSelector` de rrweb : il couvre
// aussi le contenu ajouté plus tard, y compris email, nom affiché et facturation.
const REPLAY_PRIVACY = {
  "data-replay-mask-text-selectors": JSON.stringify([ "*" ]),
  "data-replay-mask-all-inputs": "true"
}

const VISITOR_KEYS = [ "rybbit-visitor-id", "rybbit-user-id" ]
const MAX_PENDING = 50

let state = "unknown"
let loaded = false
let failed = false
let ready = false
let pending = []

// Rybbit n'accepte que des props string ou number. Booléens en "true"/"false", objets
// et tableaux en JSON, null/undefined et nombres non finis écartés.
export function rybbitProps(props) {
  return Object.entries(props ?? {}).reduce((out, [ key, value ]) => {
    if (typeof value === "string") out[key] = value
    else if (typeof value === "number") { if (Number.isFinite(value)) out[key] = value }
    else if (typeof value === "boolean") out[key] = String(value)
    else if (value !== null && value !== undefined) out[key] = JSON.stringify(value)
    return out
  }, {})
}

export function loadRybbit(src) {
  if (failed || !src) { state = "denied"; return }
  state = "accepted"
  if (loaded) return
  loaded = true

  const script = document.createElement("script")
  script.src = src
  Object.entries(REPLAY_PRIVACY).forEach(([ name, value ]) => script.setAttribute(name, value))
  script.addEventListener("load", () => {
    ready = true
    const queued = pending
    pending = []
    queued.forEach(([ name, props ]) => trackRybbit(name, props))
  })
  // Bloqueur de pub ou réseau : on abandonne sans rien casser côté Umami.
  script.addEventListener("error", () => { failed = true; state = "denied"; pending = [] })
  document.head.appendChild(script)
}

// Pas de consentement (refus, expiration, révision) : on jette la file et on efface
// l'identifiant visiteur, sans recharger. Appelé à la fin de CookieConsent.run().
export function denyRybbit() {
  state = "denied"
  pending = []
  try { VISITOR_KEYS.forEach(key => localStorage.removeItem(key)) } catch { /* storage bloqué */ }
}

// Retrait du consentement (art. 7.3 RGPD) dans la page courante : si le script tourne déjà
// on ne peut pas l'arrêter en place, donc on recharge.
export function unloadRybbit() {
  denyRybbit()
  if (loaded) window.location.reload()
}

export function trackRybbit(name, props) {
  if (state === "denied") return

  try {
    const clean = rybbitProps(props)
    if (state === "accepted" && ready) window.rybbit?.event?.(name, clean)
    else if (pending.length < MAX_PENDING) pending.push([ name, clean ])
  } catch { /* ne doit jamais casser track() ni l'envoi Umami */ }
}
